// frontend/src/pages/superAdmin/DetailedReports.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';
import api from '../../services/api';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';

const DetailedReports = ({ user, setUser }) => {
  const [loading, setLoading] = useState(true);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [schools, setSchools] = useState([]);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState('');
  const [selectedSchool, setSelectedSchool] = useState('all');
  const [reportData, setReportData] = useState([]);
  const [loadingReport, setLoadingReport] = useState(false);

  // ฟังก์ชันช่วยสำหรับการจัดการค่า null/undefined
  const safeToFixed = (value, decimals = 2) => {
    const num = parseFloat(value);
    return isNaN(num) ? '0.00' : num.toFixed(decimals);
  };

  const safeNumber = (value) => {
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // ดึงข้อมูลปีงบประมาณ
        const fiscalYearsRes = await api.get('/fiscal-years');
        setFiscalYears(fiscalYearsRes.data);
        
        // ดึงข้อมูลโรงเรียน
        const schoolsRes = await api.get('/schools');
        setSchools(schoolsRes.data);
        
        // กำหนดปีงบประมาณปัจจุบันเป็นค่าเริ่มต้น
        const activeFiscalYear = fiscalYearsRes.data.find(fy => fy.active);
        if (activeFiscalYear) {
          setSelectedFiscalYear(activeFiscalYear.id.toString());
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
        Swal.fire({
          icon: 'error',
          title: 'การโหลดข้อมูลล้มเหลว',
          text: 'ไม่สามารถโหลดข้อมูลเริ่มต้นได้',
          confirmButtonText: 'ตกลง'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  const fetchDetailedReport = async () => {
    if (!selectedFiscalYear) {
      Swal.fire({
        icon: 'warning',
        title: 'กรุณาเลือกปีงบประมาณ',
        text: 'กรุณาเลือกปีงบประมาณที่ต้องการดูรายงาน',
        confirmButtonText: 'ตกลง'
      });
      return;
    }

    try {
      setLoadingReport(true);
      
      let endpoint = `/reports/detailed-scores/${selectedFiscalYear}`;
      if (selectedSchool !== 'all') {
        endpoint = `/reports/detailed-scores/${selectedFiscalYear}/${selectedSchool}`;
      }
      
      const response = await api.get(endpoint);
      const data = Array.isArray(response.data) ? response.data : [response.data];
      
      // ตรวจสอบและทำความสะอาดข้อมูล
      const cleanedData = data.map(school => ({
        ...school,
        scores: {
          iit: {
            ...school.scores?.iit,
            questions: (school.scores?.iit?.questions || []).map(q => ({
              ...q,
              avg_score: safeNumber(q.avg_score),
              weight: safeNumber(q.weight)
            }))
          },
          eit1: {
            ...school.scores?.eit1,
            questions: (school.scores?.eit1?.questions || []).map(q => ({
              ...q,
              avg_score: safeNumber(q.avg_score),
              weight: safeNumber(q.weight)
            }))
          },
          eit2: {
            ...school.scores?.eit2,
            questions: (school.scores?.eit2?.questions || []).map(q => ({
              ...q,
              avg_score: safeNumber(q.avg_score),
              weight: safeNumber(q.weight)
            }))
          },
          oit: {
            ...school.scores?.oit,
            questions: (school.scores?.oit?.questions || []).map(q => ({
              ...q,
              avg_score: safeNumber(q.avg_score),
              weight: safeNumber(q.weight),
              evaluations_count: q.evaluations_count || 0
            }))
          }
        },
        summary: {
          ...school.summary,
          iit_score: safeNumber(school.summary?.iit_score),
          eit1_score: safeNumber(school.summary?.eit1_score),
          eit2_score: safeNumber(school.summary?.eit2_score),
          eit_total_score: safeNumber(school.summary?.eit_total_score),
          oit_score: safeNumber(school.summary?.oit_score),
          total_score: safeNumber(school.summary?.total_score),
          rating: school.summary?.rating || 'ไม่ระบุ'
        }
      }));
      
      setReportData(cleanedData);
    } catch (error) {
      console.error('Error fetching detailed report:', error);
      Swal.fire({
        icon: 'error',
        title: 'การโหลดรายงานล้มเหลว',
        text: error.response?.data?.message || 'ไม่สามารถโหลดรายงานได้ กรุณาลองใหม่ภายหลัง',
        confirmButtonText: 'ตกลง'
      });
    } finally {
      setLoadingReport(false);
    }
  };

  const exportToExcel = () => {
    if (reportData.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'ไม่มีข้อมูลสำหรับส่งออก',
        text: 'กรุณาโหลดรายงานก่อนส่งออก Excel',
        confirmButtonText: 'ตกลง'
      });
      return;
    }

    try {
      const workbook = XLSX.utils.book_new();
      const selectedFiscalYearData = fiscalYears.find(fy => fy.id.toString() === selectedFiscalYear);
      const fiscalYearName = selectedFiscalYearData?.year || 'Unknown';
      
      // สร้างแผ่นงานสรุปภาพรวม
      const summaryData = [
        ['รายงานสรุปคะแนน ITA รายข้อ'],
        ['ปีงบประมาณ:', fiscalYearName],
        ['วันที่สร้างรายงาน:', new Date().toLocaleDateString('th-TH')],
        ['จำนวนโรงเรียน:', reportData.length],
        [],
        ['รหัสโรงเรียน', 'ชื่อโรงเรียน', 'คะแนน IIT', 'คะแนน EIT1', 'คะแนน EIT2', 'คะแนน EIT รวม', 'คะแนน OIT', 'คะแนนรวม', 'ผลการประเมิน']
      ];

      reportData.forEach((school, index) => {
        summaryData.push([
          school.school?.code || '',
          school.school?.name || '',
          safeToFixed(school.summary.iit_score),
          safeToFixed(school.summary.eit1_score),
          safeToFixed(school.summary.eit2_score),
          safeToFixed(school.summary.eit_total_score),
          safeToFixed(school.summary.oit_score),
          safeToFixed(school.summary.total_score),
          school.summary.rating
        ]);
      });

      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      summarySheet['!cols'] = [
        { wch: 15 }, { wch: 30 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, 
        { wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 15 }
      ];
      
      XLSX.utils.book_append_sheet(workbook, summarySheet, 'สรุปภาพรวม');

      // สร้างแผ่นงานรายละเอียดแต่ละโรงเรียน
      reportData.forEach((schoolData, index) => {
        const schoolName = schoolData.school?.name || `โรงเรียนที่ ${index + 1}`;
        const sheetName = schoolName.length > 25 
          ? schoolName.substring(0, 25) + '...' 
          : schoolName;
        
        const detailData = [
          [`รายละเอียดคะแนน - ${schoolData.school?.name || ''}`],
          ['รหัสโรงเรียน:', schoolData.school?.code || ''],
          ['ชื่อโรงเรียน:', schoolData.school?.name || ''],
          ['ผู้อำนวยการ:', schoolData.school?.director || ''],
          [],
          ['=== คะแนน IIT (30 คะแนน) ==='],
          ['ข้อ', 'คำถาม', 'คะแนนเฉลี่ย', 'น้ำหนัก', 'จำนวนผู้ตอบ']
        ];

        if (schoolData.scores?.iit?.questions) {
          schoolData.scores.iit.questions.forEach((q, qIndex) => {
            detailData.push([
              qIndex + 1,
              q.question_text || '',
              safeToFixed(q.avg_score),
              safeToFixed(q.weight),
              schoolData.scores.iit.respondent_count || 0
            ]);
          });
        }

        detailData.push(
          [],
          ['คะแนนรวม IIT:', safeToFixed(schoolData.summary.iit_score)],
          [],
          ['=== คะแนน EIT1 (15 คะแนน) ==='],
          ['ข้อ', 'คำถาม', 'คะแนนเฉลี่ย', 'น้ำหนัก', 'จำนวนผู้ตอบ']
        );

        if (schoolData.scores?.eit1?.questions) {
          schoolData.scores.eit1.questions.forEach((q, qIndex) => {
            detailData.push([
              qIndex + 1,
              q.question_text || '',
              safeToFixed(q.avg_score),
              safeToFixed(q.weight),
              schoolData.scores.eit1.respondent_count || 0
            ]);
          });
        }

        detailData.push(
          [],
          ['คะแนนรวม EIT1:', safeToFixed(schoolData.summary.eit1_score)],
          [],
          ['=== คะแนน EIT2 (15 คะแนน) ==='],
          ['ข้อ', 'คำถาม', 'คะแนนเฉลี่ย', 'น้ำหนัก', 'จำนวนผู้ตอบ']
        );

        if (schoolData.scores?.eit2?.questions) {
          schoolData.scores.eit2.questions.forEach((q, qIndex) => {
            detailData.push([
              qIndex + 1,
              q.question_text || '',
              safeToFixed(q.avg_score),
              safeToFixed(q.weight),
              schoolData.scores.eit2.respondent_count || 0
            ]);
          });
        }

        detailData.push(
          [],
          ['คะแนนรวม EIT2:', safeToFixed(schoolData.summary.eit2_score)],
          [],
          ['=== คะแนน OIT (40 คะแนน) ==='],
          ['ข้อ', 'คำถาม', 'คะแนนเฉลี่ย', 'น้ำหนัก', 'จำนวนการประเมิน']
        );

        if (schoolData.scores?.oit?.questions) {
          schoolData.scores.oit.questions.forEach((q, qIndex) => {
            detailData.push([
              qIndex + 1,
              q.question_text || '',
              safeToFixed(q.avg_score),
              safeToFixed(q.weight),
              q.evaluations_count || 0
            ]);
          });
        }

        detailData.push(
          [],
          ['คะแนนรวม OIT:', safeToFixed(schoolData.summary.oit_score)],
          [],
          ['=== สรุปคะแนนรวม ==='],
          ['IIT (30):', safeToFixed(schoolData.summary.iit_score)],
          ['EIT1 (15):', safeToFixed(schoolData.summary.eit1_score)],
          ['EIT2 (15):', safeToFixed(schoolData.summary.eit2_score)],
          ['EIT รวม (30):', safeToFixed(schoolData.summary.eit_total_score)],
          ['OIT (40):', safeToFixed(schoolData.summary.oit_score)],
          ['คะแนนรวม (100):', safeToFixed(schoolData.summary.total_score)],
          ['ผลการประเมิน:', schoolData.summary.rating]
        );

        const detailSheet = XLSX.utils.aoa_to_sheet(detailData);
        detailSheet['!cols'] = [
          { wch: 5 }, { wch: 50 }, { wch: 15 }, { wch: 10 }, { wch: 15 }
        ];
        
        try {
          XLSX.utils.book_append_sheet(workbook, detailSheet, sheetName);
        } catch (error) {
          console.warn(`Cannot create sheet for ${schoolName}:`, error);
          XLSX.utils.book_append_sheet(workbook, detailSheet, `School_${index + 1}`);
        }
      });

      // ส่งออกไฟล์
      const fileName = `รายงานคะแนน_ITA_สรุป_${fiscalYearName}_${new Date().getTime()}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      Swal.fire({
        icon: 'success',
        title: 'ส่งออก Excel สำเร็จ',
        text: `ไฟล์ ${fileName} ถูกส่งออกเรียบร้อยแล้ว`,
        confirmButtonText: 'ตกลง'
      });
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      Swal.fire({
        icon: 'error',
        title: 'การส่งออก Excel ล้มเหลว',
        text: 'ไม่สามารถส่งออกไฟล์ Excel ได้ กรุณาลองใหม่ภายหลัง',
        confirmButtonText: 'ตกลง'
      });
    }
  };

  // ฟังก์ชันส่งออก Excel แบบละเอียดทุกประเด็น
  const exportDetailedExcel = () => {
    if (reportData.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'ไม่มีข้อมูลสำหรับส่งออก',
        text: 'กรุณาโหลดรายงานก่อนส่งออก Excel',
        confirmButtonText: 'ตกลง'
      });
      return;
    }

    try {
      const workbook = XLSX.utils.book_new();
      const selectedFiscalYearData = fiscalYears.find(fy => fy.id.toString() === selectedFiscalYear);
      const fiscalYearName = selectedFiscalYearData?.year || 'Unknown';

      // 1. แผ่นสรุปภาพรวมทุกโรงเรียน
      const overviewData = [
        ['รายงานคะแนน ITA รายละเอียดทุกประเด็น'],
        ['ปีงบประมาณ:', fiscalYearName],
        ['วันที่สร้างรายงาน:', new Date().toLocaleDateString('th-TH')],
        ['จำนวนโรงเรียน:', reportData.length],
        [],
        ['อันดับ', 'รหัสโรงเรียน', 'ชื่อโรงเรียน', 'ผู้อำนวยการ', 'IIT (30)', 'EIT1 (15)', 'EIT2 (15)', 'EIT รวม (30)', 'OIT (40)', 'คะแนนรวม (100)', 'ผลการประเมิน']
      ];

      reportData.forEach((school, index) => {
        overviewData.push([
          index + 1,
          school.school?.code || '',
          school.school?.name || '',
          school.school?.director || '',
          safeToFixed(school.summary.iit_score),
          safeToFixed(school.summary.eit1_score),
          safeToFixed(school.summary.eit2_score),
          safeToFixed(school.summary.eit_total_score),
          safeToFixed(school.summary.oit_score),
          safeToFixed(school.summary.total_score),
          school.summary.rating
        ]);
      });

      const overviewSheet = XLSX.utils.aoa_to_sheet(overviewData);
      overviewSheet['!cols'] = [
        { wch: 8 }, { wch: 12 }, { wch: 30 }, { wch: 20 }, { wch: 10 }, 
        { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 15 }
      ];
      XLSX.utils.book_append_sheet(workbook, overviewSheet, 'สรุปภาพรวม');

      // 2. แผ่นเปรียบเทียบคะแนน IIT รายข้อทุกโรงเรียน
      if (reportData.length > 0 && reportData[0].scores?.iit?.questions?.length > 0) {
        const iitData = [
          ['เปรียบเทียบคะแนน IIT รายข้อทุกโรงเรียน'],
          ['ปีงบประมาณ:', fiscalYearName],
          []
        ];

        // สร้างหัวข้อ
        const iitHeaders = ['ข้อ', 'คำถาม', 'น้ำหนัก'];
        reportData.forEach(school => {
          iitHeaders.push(`${school.school?.code || 'N/A'}\n(${school.school?.name || 'N/A'})`);
        });
        iitData.push(iitHeaders);

        // สร้างข้อมูลแต่ละคำถาม
        const maxQuestions = Math.max(...reportData.map(s => s.scores?.iit?.questions?.length || 0));
        for (let i = 0; i < maxQuestions; i++) {
          const questionData = [];
          const firstSchoolQuestion = reportData[0].scores?.iit?.questions?.[i];
          
          questionData.push(i + 1); // ข้อ
          questionData.push(firstSchoolQuestion?.question_text || 'N/A'); // คำถาม
          questionData.push(safeToFixed(firstSchoolQuestion?.weight || 0)); // น้ำหนัก
          
          // คะแนนแต่ละโรงเรียน
          reportData.forEach(school => {
            const question = school.scores?.iit?.questions?.[i];
            questionData.push(safeToFixed(question?.avg_score || 0));
          });
          
          iitData.push(questionData);
        }

        // แถวสรุปคะแนนรวม IIT
        const iitSummaryRow = ['', 'คะแนนรวม IIT', ''];
        reportData.forEach(school => {
          iitSummaryRow.push(safeToFixed(school.summary.iit_score));
        });
        iitData.push([]);
        iitData.push(iitSummaryRow);

        const iitSheet = XLSX.utils.aoa_to_sheet(iitData);
        const iitCols = [{ wch: 5 }, { wch: 50 }, { wch: 10 }];
        reportData.forEach(() => iitCols.push({ wch: 15 }));
        iitSheet['!cols'] = iitCols;
        XLSX.utils.book_append_sheet(workbook, iitSheet, 'เปรียบเทียบ IIT');
      }

      // 3. แผ่นเปรียบเทียบคะแนน EIT1 รายข้อทุกโรงเรียน
      if (reportData.length > 0 && reportData[0].scores?.eit1?.questions?.length > 0) {
        const eit1Data = [
          ['เปรียบเทียบคะแนน EIT1 รายข้อทุกโรงเรียน'],
          ['ปีงบประมาณ:', fiscalYearName],
          []
        ];

        const eit1Headers = ['ข้อ', 'คำถาม', 'น้ำหนัก'];
        reportData.forEach(school => {
          eit1Headers.push(`${school.school?.code || 'N/A'}\n(${school.school?.name || 'N/A'})`);
        });
        eit1Data.push(eit1Headers);

        const maxEIT1Questions = Math.max(...reportData.map(s => s.scores?.eit1?.questions?.length || 0));
        for (let i = 0; i < maxEIT1Questions; i++) {
          const questionData = [];
          const firstSchoolQuestion = reportData[0].scores?.eit1?.questions?.[i];
          
          questionData.push(i + 1);
          questionData.push(firstSchoolQuestion?.question_text || 'N/A');
          questionData.push(safeToFixed(firstSchoolQuestion?.weight || 0));
          
          reportData.forEach(school => {
            const question = school.scores?.eit1?.questions?.[i];
            questionData.push(safeToFixed(question?.avg_score || 0));
          });
          
          eit1Data.push(questionData);
        }

        const eit1SummaryRow = ['', 'คะแนนรวม EIT1', ''];
        reportData.forEach(school => {
          eit1SummaryRow.push(safeToFixed(school.summary.eit1_score));
        });
        eit1Data.push([]);
        eit1Data.push(eit1SummaryRow);

        const eit1Sheet = XLSX.utils.aoa_to_sheet(eit1Data);
        const eit1Cols = [{ wch: 5 }, { wch: 50 }, { wch: 10 }];
        reportData.forEach(() => eit1Cols.push({ wch: 15 }));
        eit1Sheet['!cols'] = eit1Cols;
        XLSX.utils.book_append_sheet(workbook, eit1Sheet, 'เปรียบเทียบ EIT1');
      }

      // 4. แผ่นเปรียบเทียบคะแนน EIT2 รายข้อทุกโรงเรียน
      if (reportData.length > 0 && reportData[0].scores?.eit2?.questions?.length > 0) {
        const eit2Data = [
          ['เปรียบเทียบคะแนน EIT2 รายข้อทุกโรงเรียน'],
          ['ปีงบประมาณ:', fiscalYearName],
          []
        ];

        const eit2Headers = ['ข้อ', 'คำถาม', 'น้ำหนัก'];
        reportData.forEach(school => {
          eit2Headers.push(`${school.school?.code || 'N/A'}\n(${school.school?.name || 'N/A'})`);
        });
        eit2Data.push(eit2Headers);

        const maxEIT2Questions = Math.max(...reportData.map(s => s.scores?.eit2?.questions?.length || 0));
        for (let i = 0; i < maxEIT2Questions; i++) {
          const questionData = [];
          const firstSchoolQuestion = reportData[0].scores?.eit2?.questions?.[i];
          
          questionData.push(i + 1);
          questionData.push(firstSchoolQuestion?.question_text || 'N/A');
          questionData.push(safeToFixed(firstSchoolQuestion?.weight || 0));
          
          reportData.forEach(school => {
            const question = school.scores?.eit2?.questions?.[i];
            questionData.push(safeToFixed(question?.avg_score || 0));
          });
          
          eit2Data.push(questionData);
        }

        const eit2SummaryRow = ['', 'คะแนนรวม EIT2', ''];
        reportData.forEach(school => {
          eit2SummaryRow.push(safeToFixed(school.summary.eit2_score));
        });
        eit2Data.push([]);
        eit2Data.push(eit2SummaryRow);

        const eit2Sheet = XLSX.utils.aoa_to_sheet(eit2Data);
        const eit2Cols = [{ wch: 5 }, { wch: 50 }, { wch: 10 }];
        reportData.forEach(() => eit2Cols.push({ wch: 15 }));
        eit2Sheet['!cols'] = eit2Cols;
        XLSX.utils.book_append_sheet(workbook, eit2Sheet, 'เปรียบเทียบ EIT2');
      }

      // 5. แผ่นเปรียบเทียบคะแนน OIT รายข้อทุกโรงเรียน
      if (reportData.length > 0 && reportData[0].scores?.oit?.questions?.length > 0) {
        const oitData = [
          ['เปรียบเทียบคะแนน OIT รายข้อทุกโรงเรียน'],
          ['ปีงบประมาณ:', fiscalYearName],
          []
        ];

        const oitHeaders = ['ข้อ', 'คำถาม', 'น้ำหนัก'];
        reportData.forEach(school => {
          oitHeaders.push(`${school.school?.code || 'N/A'}\n(${school.school?.name || 'N/A'})`);
        });
        oitData.push(oitHeaders);

        const maxOITQuestions = Math.max(...reportData.map(s => s.scores?.oit?.questions?.length || 0));
        for (let i = 0; i < maxOITQuestions; i++) {
          const questionData = [];
          const firstSchoolQuestion = reportData[0].scores?.oit?.questions?.[i];
          
          questionData.push(i + 1);
          questionData.push(firstSchoolQuestion?.question_text || 'N/A');
          questionData.push(safeToFixed(firstSchoolQuestion?.weight || 0));
          
          reportData.forEach(school => {
            const question = school.scores?.oit?.questions?.[i];
            questionData.push(safeToFixed(question?.avg_score || 0));
          });
          
          oitData.push(questionData);
        }

        const oitSummaryRow = ['', 'คะแนนรวม OIT', ''];
        reportData.forEach(school => {
          oitSummaryRow.push(safeToFixed(school.summary.oit_score));
        });
        oitData.push([]);
        oitData.push(oitSummaryRow);

        const oitSheet = XLSX.utils.aoa_to_sheet(oitData);
        const oitCols = [{ wch: 5 }, { wch: 50 }, { wch: 10 }];
        reportData.forEach(() => oitCols.push({ wch: 15 }));
        oitSheet['!cols'] = oitCols;
        XLSX.utils.book_append_sheet(workbook, oitSheet, 'เปรียบเทียบ OIT');
      }

      // 6. แผ่นสถิติและการวิเคราะห์
      const statsData = [
        ['สถิติและการวิเคราะห์คะแนน'],
        ['ปีงบประมาณ:', fiscalYearName],
        ['จำนวนโรงเรียนที่ประเมิน:', reportData.length],
        [],
        ['=== สถิติคะแนนรวม ==='],
        ['ประเภท', 'คะแนนเฉลี่ย', 'คะแนนสูงสุด', 'คะแนนต่ำสุด', 'ส่วนเบี่ยงเบนมาตรฐาน']
      ];

      // คำนวณสถิติสำหรับแต่ละประเภท
      const categories = [
        { name: 'IIT (30)', scores: reportData.map(s => s.summary.iit_score) },
        { name: 'EIT1 (15)', scores: reportData.map(s => s.summary.eit1_score) },
        { name: 'EIT2 (15)', scores: reportData.map(s => s.summary.eit2_score) },
        { name: 'EIT รวม (30)', scores: reportData.map(s => s.summary.eit_total_score) },
        { name: 'OIT (40)', scores: reportData.map(s => s.summary.oit_score) },
        { name: 'คะแนนรวม (100)', scores: reportData.map(s => s.summary.total_score) }
      ];

      categories.forEach(category => {
        const scores = category.scores;
        const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
        const max = Math.max(...scores);
        const min = Math.min(...scores);
        const variance = scores.reduce((a, b) => a + Math.pow(b - avg, 2), 0) / scores.length;
        const stdDev = Math.sqrt(variance);

        statsData.push([
          category.name,
          safeToFixed(avg),
          safeToFixed(max),
          safeToFixed(min),
          safeToFixed(stdDev)
        ]);
      });

      // เพิ่มการแจกแจงตามระดับการประเมิน
      statsData.push(
        [],
        ['=== การแจกแจงตามผลการประเมิน ==='],
        ['ผลการประเมิน', 'จำนวนโรงเรียน', 'เปอร์เซ็นต์']
      );

      const ratingCounts = {};
      reportData.forEach(school => {
        const rating = school.summary.rating;
        ratingCounts[rating] = (ratingCounts[rating] || 0) + 1;
      });

      Object.entries(ratingCounts).forEach(([rating, count]) => {
        const percentage = ((count / reportData.length) * 100).toFixed(2);
        statsData.push([rating, count, `${percentage}%`]);
      });

      const statsSheet = XLSX.utils.aoa_to_sheet(statsData);
      statsSheet['!cols'] = [
        { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 20 }
      ];
      XLSX.utils.book_append_sheet(workbook, statsSheet, 'สถิติและการวิเคราะห์');

      // 7. เพิ่มแผ่นรายละเอียดแต่ละโรงเรียนแบบย่อ
      reportData.forEach((schoolData, index) => {
        const schoolCode = schoolData.school?.code || `SCH${index + 1}`;
        const detailData = [
          [`${schoolCode} - ${schoolData.school?.name || ''}`],
          ['คะแนนสรุป'],
          ['IIT:', safeToFixed(schoolData.summary.iit_score)],
          ['EIT1:', safeToFixed(schoolData.summary.eit1_score)],
          ['EIT2:', safeToFixed(schoolData.summary.eit2_score)],
          ['EIT รวม:', safeToFixed(schoolData.summary.eit_total_score)],
          ['OIT:', safeToFixed(schoolData.summary.oit_score)],
          ['รวมทั้งหมด:', safeToFixed(schoolData.summary.total_score)],
          ['ผลการประเมิน:', schoolData.summary.rating],
          [],
          ['รายละเอียดคะแนนรายข้อ']
        ];

        // เพิ่มรายละเอียด IIT
        if (schoolData.scores?.iit?.questions?.length > 0) {
          detailData.push(['=== IIT ===']);
          schoolData.scores.iit.questions.forEach((q, i) => {
            detailData.push([`${i + 1}. ${q.question_text}`, safeToFixed(q.avg_score)]);
          });
          detailData.push([]);
        }

        // เพิ่มรายละเอียด EIT1
        if (schoolData.scores?.eit1?.questions?.length > 0) {
          detailData.push(['=== EIT1 ===']);
          schoolData.scores.eit1.questions.forEach((q, i) => {
            detailData.push([`${i + 1}. ${q.question_text}`, safeToFixed(q.avg_score)]);
          });
          detailData.push([]);
        }

        // เพิ่มรายละเอียด EIT2
        if (schoolData.scores?.eit2?.questions?.length > 0) {
          detailData.push(['=== EIT2 ===']);
          schoolData.scores.eit2.questions.forEach((q, i) => {
            detailData.push([`${i + 1}. ${q.question_text}`, safeToFixed(q.avg_score)]);
          });
          detailData.push([]);
        }

        // เพิ่มรายละเอียด OIT
        if (schoolData.scores?.oit?.questions?.length > 0) {
          detailData.push(['=== OIT ===']);
          schoolData.scores.oit.questions.forEach((q, i) => {
            detailData.push([`${i + 1}. ${q.question_text}`, safeToFixed(q.avg_score)]);
          });
        }

        const detailSheet = XLSX.utils.aoa_to_sheet(detailData);
        detailSheet['!cols'] = [{ wch: 60 }, { wch: 15 }];
        
        try {
          XLSX.utils.book_append_sheet(workbook, detailSheet, schoolCode);
        } catch (error) {
          console.warn(`Cannot create sheet for ${schoolCode}:`, error);
          XLSX.utils.book_append_sheet(workbook, detailSheet, `SCH_${index + 1}`);
        }
      });

      // ส่งออกไฟล์
      const fileName = `รายงานคะแนน_ITA_ละเอียดทุกประเด็น_${fiscalYearName}_${new Date().getTime()}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      Swal.fire({
        icon: 'success',
        title: 'ส่งออก Excel แบบละเอียดสำเร็จ',
        text: `ไฟล์ ${fileName} ถูกส่งออกเรียบร้อยแล้ว`,
        confirmButtonText: 'ตกลง'
      });
    } catch (error) {
      console.error('Error exporting detailed Excel:', error);
      Swal.fire({
        icon: 'error',
        title: 'การส่งออก Excel ล้มเหลว',
        text: 'ไม่สามารถส่งออกไฟล์ Excel ได้ กรุณาลองใหม่ภายหลัง',
        confirmButtonText: 'ตกลง'
      });
    }
  };

  const getScoreBadgeClass = (score, maxScore = 1) => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 80) return 'bg-success';
    if (percentage >= 60) return 'bg-warning';
    return 'bg-danger';
  };

  const getTotalScoreBadgeClass = (score) => {
    if (score >= 95) return 'bg-success';
    if (score >= 85) return 'bg-info';
    if (score >= 75) return 'bg-warning';
    if (score >= 65) return 'bg-danger';
    return 'bg-dark';
  };

  if (loading) {
    return (
      <div className="wrapper">
        <Header user={user} title="สรุปคะแนนรายข้อ" />
        <Sidebar user={user} />
        <div className="content-wrapper">
          <div className="content-header">
            <div className="container-fluid">
              <div className="row mb-2">
                <div className="col-sm-6">
                  <h1 className="m-0">สรุปคะแนนรายข้อ</h1>
                </div>
              </div>
            </div>
          </div>
          <section className="content">
            <div className="container-fluid">
              <div className="d-flex justify-content-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">กำลังโหลด...</span>
                </div>
              </div>
            </div>
          </section>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="wrapper">
      <Header user={user} title="สรุปคะแนนรายข้อ" />
      <Sidebar user={user} />
      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">สรุปคะแนนรายข้อ</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-end">
                  <li className="breadcrumb-item">
                    <Link to="/super-admin/dashboard">หน้าหลัก</Link>
                  </li>
                  <li className="breadcrumb-item active">สรุปคะแนนรายข้อ</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        <section className="content">
          <div className="container-fluid">
            {/* ตัวกรองข้อมูล */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">
                  <i className="fas fa-filter me-1"></i> ตัวกรองข้อมูล
                </h3>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-3">
                    <label htmlFor="fiscalYear" className="form-label">ปีงบประมาณ</label>
                    <select
                      id="fiscalYear"
                      className="form-select"
                      value={selectedFiscalYear}
                      onChange={(e) => setSelectedFiscalYear(e.target.value)}
                    >
                      <option value="">เลือกปีงบประมาณ</option>
                      {fiscalYears.map(fy => (
                        <option key={fy.id} value={fy.id}>
                          {fy.year} {fy.active && '(ปัจจุบัน)'}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="school" className="form-label">โรงเรียน</label>
                    <select
                      id="school"
                      className="form-select"
                      value={selectedSchool}
                      onChange={(e) => setSelectedSchool(e.target.value)}
                    >
                      <option value="all">ทุกโรงเรียน</option>
                      {schools.map(school => (
                        <option key={school.id} value={school.id}>
                          {school.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6 d-flex align-items-end">
                    <button
                      type="button"
                      className="btn btn-primary me-2"
                      onClick={fetchDetailedReport}
                      disabled={loadingReport}
                    >
                      {loadingReport ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1"></span>
                          กำลังโหลด...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-search me-1"></i>
                          แสดงรายงาน
                        </>
                      )}
                    </button>
                    <div className="btn-group">
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={exportToExcel}
                        disabled={reportData.length === 0}
                      >
                        <i className="fas fa-file-excel me-1"></i>
                        ส่งออก Excel (สรุป)
                      </button>
                      <button
                        type="button"
                        className="btn btn-info"
                        onClick={exportDetailedExcel}
                        disabled={reportData.length === 0}
                        title="ส่งออก Excel แบบละเอียดทุกประเด็น พร้อมการเปรียบเทียบและสถิติ"
                      >
                        <i className="fas fa-chart-line me-1"></i>
                        ส่งออก Excel (ละเอียดทุกประเด็น)
                      </button>
                    </div>
                  </div>
                </div>
                {reportData.length > 0 && (
                  <div className="row mt-3">
                    <div className="col-12">
                      <div className="alert alert-info">
                        <h6 className="alert-heading mb-2">
                          <i className="fas fa-info-circle me-1"></i> คำอธิบายการส่งออก Excel
                        </h6>
                        <div className="row">
                          <div className="col-md-6">
                            <strong>ส่งออก Excel (สรุป):</strong>
                            <ul className="mb-0 small">
                              <li>สรุปคะแนนภาพรวมทุกโรงเรียน</li>
                              <li>รายละเอียดคะแนนแต่ละโรงเรียน</li>
                            </ul>
                          </div>
                          <div className="col-md-6">
                            <strong>ส่งออก Excel (ละเอียดทุกประเด็น):</strong>
                            <ul className="mb-0 small">
                              <li>เปรียบเทียบคะแนนรายข้อระหว่างโรงเรียน (IIT, EIT1, EIT2, OIT)</li>
                              <li>สถิติและการวิเคราะห์คะแนน</li>
                              <li>รายละเอียดแต่ละโรงเรียนในแผ่นงานแยก</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* แสดงผลรายงาน */}
            {reportData.length > 0 && (
              <>
                {/* สรุปภาพรวม */}
                <div className="card">
                  <div className="card-header bg-primary text-white">
                    <h3 className="card-title">
                      <i className="fas fa-chart-bar me-1"></i> สรุปคะแนนภาพรวม
                    </h3>
                    <div className="card-tools">
                      <span className="badge bg-light text-dark">
                        จำนวน {reportData.length} โรงเรียน
                      </span>
                    </div>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-striped table-hover mb-0">
                      <thead className="table-dark">
                        <tr>
                          <th className="text-center">อันดับ</th>
                          <th>รหัสโรงเรียน</th>
                          <th>ชื่อโรงเรียน</th>
                          <th className="text-center">IIT (30)</th>
                          <th className="text-center">EIT1 (15)</th>
                          <th className="text-center">EIT2 (15)</th>
                          <th className="text-center">EIT รวม (30)</th>
                          <th className="text-center">OIT (40)</th>
                          <th className="text-center">คะแนนรวม (100)</th>
                          <th className="text-center">ผลการประเมิน</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.map((school, index) => (
                          <tr key={school.school?.id || index}>
                            <td className="text-center">
                              <span className={`badge ${
                                index === 0 ? 'bg-warning' : 
                                index === 1 ? 'bg-secondary' : 
                                index === 2 ? 'bg-warning' : 'bg-light text-dark'
                              }`}>
                                #{index + 1}
                              </span>
                            </td>
                            <td>{school.school?.code || 'N/A'}</td>
                            <td>{school.school?.name || 'N/A'}</td>
                            <td className="text-center">
                              <span className={`badge ${getScoreBadgeClass(school.summary.iit_score, 30)}`}>
                                {safeToFixed(school.summary.iit_score)}
                              </span>
                            </td>
                            <td className="text-center">
                              <span className={`badge ${getScoreBadgeClass(school.summary.eit1_score, 15)}`}>
                                {safeToFixed(school.summary.eit1_score)}
                              </span>
                            </td>
                            <td className="text-center">
                              <span className={`badge ${getScoreBadgeClass(school.summary.eit2_score, 15)}`}>
                                {safeToFixed(school.summary.eit2_score)}
                              </span>
                            </td>
                            <td className="text-center">
                              <span className={`badge ${getScoreBadgeClass(school.summary.eit_total_score, 30)}`}>
                                {safeToFixed(school.summary.eit_total_score)}
                              </span>
                            </td>
                            <td className="text-center">
                              <span className={`badge ${getScoreBadgeClass(school.summary.oit_score, 40)}`}>
                                {safeToFixed(school.summary.oit_score)}
                              </span>
                            </td>
                            <td className="text-center">
                              <span className={`badge ${getTotalScoreBadgeClass(school.summary.total_score)}`}>
                                {safeToFixed(school.summary.total_score)}
                              </span>
                            </td>
                            <td className="text-center">
                              <span className={`badge ${getTotalScoreBadgeClass(school.summary.total_score)}`}>
                                {school.summary.rating}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* รายละเอียดแต่ละโรงเรียน */}
                {reportData.map((schoolData, schoolIndex) => (
                  <div key={schoolData.school?.id || schoolIndex} className="card">
                    <div className="card-header bg-secondary text-white">
                      <h3 className="card-title">
                        <i className="fas fa-school me-1"></i>
                        รายละเอียดคะแนน - {schoolData.school?.name || `โรงเรียนที่ ${schoolIndex + 1}`}
                      </h3>
                      <div className="card-tools">
                        <button type="button" className="btn btn-tool" data-bs-toggle="collapse" data-bs-target={`#school-${schoolData.school?.id || schoolIndex}`}>
                          <i className="fas fa-minus"></i>
                        </button>
                      </div>
                    </div>
                    <div id={`school-${schoolData.school?.id || schoolIndex}`} className="collapse show">
                      <div className="card-body">
                        <div className="row mb-4">
                          <div className="col-md-6">
                            <h5>ข้อมูลโรงเรียน</h5>
                            <p><strong>รหัส:</strong> {schoolData.school?.code || 'N/A'}</p>
                            <p><strong>ชื่อ:</strong> {schoolData.school?.name || 'N/A'}</p>
                            <p><strong>ผู้อำนวยการ:</strong> {schoolData.school?.director || 'ไม่ระบุ'}</p>
                          </div>
                          <div className="col-md-6">
                            <h5>สรุปคะแนน</h5>
                            <div className="row text-center">
                              <div className="col-3">
                                <div className="bg-light p-2 rounded">
                                  <small>IIT</small><br/>
                                  <strong>{safeToFixed(schoolData.summary.iit_score)}</strong>
                                </div>
                              </div>
                              <div className="col-3">
                                <div className="bg-light p-2 rounded">
                                  <small>EIT</small><br/>
                                  <strong>{safeToFixed(schoolData.summary.eit_total_score)}</strong>
                                </div>
                              </div>
                              <div className="col-3">
                                <div className="bg-light p-2 rounded">
                                  <small>OIT</small><br/>
                                  <strong>{safeToFixed(schoolData.summary.oit_score)}</strong>
                                </div>
                              </div>
                              <div className="col-3">
                                <div className="bg-primary text-white p-2 rounded">
                                  <small>รวม</small><br/>
                                  <strong>{safeToFixed(schoolData.summary.total_score)}</strong>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* แท็บสำหรับแต่ละประเภท */}
                        <ul className="nav nav-tabs" id={`schoolTab${schoolData.school?.id || schoolIndex}`} role="tablist">
                          <li className="nav-item" role="presentation">
                            <button className="nav-link active" id={`iit-tab-${schoolData.school?.id || schoolIndex}`} data-bs-toggle="tab" data-bs-target={`#iit-${schoolData.school?.id || schoolIndex}`} type="button" role="tab">
                              IIT (30 คะแนน)
                            </button>
                          </li>
                          <li className="nav-item" role="presentation">
                            <button className="nav-link" id={`eit1-tab-${schoolData.school?.id || schoolIndex}`} data-bs-toggle="tab" data-bs-target={`#eit1-${schoolData.school?.id || schoolIndex}`} type="button" role="tab">
                              EIT1 (15 คะแนน)
                            </button>
                          </li>
                          <li className="nav-item" role="presentation">
                            <button className="nav-link" id={`eit2-tab-${schoolData.school?.id || schoolIndex}`} data-bs-toggle="tab" data-bs-target={`#eit2-${schoolData.school?.id || schoolIndex}`} type="button" role="tab">
                              EIT2 (15 คะแนน)
                            </button>
                          </li>
                          <li className="nav-item" role="presentation">
                            <button className="nav-link" id={`oit-tab-${schoolData.school?.id || schoolIndex}`} data-bs-toggle="tab" data-bs-target={`#oit-${schoolData.school?.id || schoolIndex}`} type="button" role="tab">
                              OIT (40 คะแนน)
                            </button>
                          </li>
                        </ul>

                        <div className="tab-content" id={`schoolTabContent${schoolData.school?.id || schoolIndex}`}>
                          {/* IIT Tab */}
                          <div className="tab-pane fade show active" id={`iit-${schoolData.school?.id || schoolIndex}`} role="tabpanel">
                            <div className="table-responsive mt-3">
                              <table className="table table-sm table-striped">
                                <thead>
                                  <tr>
                                    <th className="text-center">ข้อ</th>
                                    <th>คำถาม</th>
                                    <th className="text-center">คะแนนเฉลี่ย</th>
                                    <th className="text-center">น้ำหนัก</th>
                                    <th className="text-center">ผู้ตอบ</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(schoolData.scores?.iit?.questions || []).map((q, index) => (
                                    <tr key={q.id || index}>
                                      <td className="text-center">{index + 1}</td>
                                      <td>{q.question_text || 'N/A'}</td>
                                      <td className="text-center">
                                        <span className={`badge ${getScoreBadgeClass(q.avg_score)}`}>
                                          {safeToFixed(q.avg_score)}
                                        </span>
                                      </td>
                                      <td className="text-center">{safeToFixed(q.weight)}</td>
                                      <td className="text-center">{schoolData.scores?.iit?.respondent_count || 0}</td>
                                    </tr>
                                  ))}
                                  {(!schoolData.scores?.iit?.questions || schoolData.scores.iit.questions.length === 0) && (
                                    <tr>
                                      <td colSpan="5" className="text-center text-muted">ไม่มีข้อมูลคำถาม IIT</td>
                                    </tr>
                                  )}
                                </tbody>
                                <tfoot>
                                  <tr className="table-primary">
                                    <td colSpan="4" className="text-end"><strong>คะแนนรวม IIT:</strong></td>
                                    <td className="text-center"><strong>{safeToFixed(schoolData.summary.iit_score)}</strong></td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          </div>

                          {/* EIT1 Tab */}
                          <div className="tab-pane fade" id={`eit1-${schoolData.school?.id || schoolIndex}`} role="tabpanel">
                            <div className="table-responsive mt-3">
                              <table className="table table-sm table-striped">
                                <thead>
                                  <tr>
                                    <th className="text-center">ข้อ</th>
                                    <th>คำถาม</th>
                                    <th className="text-center">คะแนนเฉลี่ย</th>
                                    <th className="text-center">น้ำหนัก</th>
                                    <th className="text-center">ผู้ตอบ</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(schoolData.scores?.eit1?.questions || []).map((q, index) => (
                                    <tr key={q.id || index}>
                                      <td className="text-center">{index + 1}</td>
                                      <td>{q.question_text || 'N/A'}</td>
                                      <td className="text-center">
                                        <span className={`badge ${getScoreBadgeClass(q.avg_score)}`}>
                                          {safeToFixed(q.avg_score)}
                                        </span>
                                      </td>
                                      <td className="text-center">{safeToFixed(q.weight)}</td>
                                      <td className="text-center">{schoolData.scores?.eit1?.respondent_count || 0}</td>
                                    </tr>
                                  ))}
                                  {(!schoolData.scores?.eit1?.questions || schoolData.scores.eit1.questions.length === 0) && (
                                    <tr>
                                      <td colSpan="5" className="text-center text-muted">ไม่มีข้อมูลคำถาม EIT1</td>
                                    </tr>
                                  )}
                                </tbody>
                                <tfoot>
                                  <tr className="table-info">
                                    <td colSpan="4" className="text-end"><strong>คะแนนรวม EIT1:</strong></td>
                                    <td className="text-center"><strong>{safeToFixed(schoolData.summary.eit1_score)}</strong></td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          </div>

                          {/* EIT2 Tab */}
                          <div className="tab-pane fade" id={`eit2-${schoolData.school?.id || schoolIndex}`} role="tabpanel">
                            <div className="table-responsive mt-3">
                              <table className="table table-sm table-striped">
                                <thead>
                                  <tr>
                                    <th className="text-center">ข้อ</th>
                                    <th>คำถาม</th>
                                    <th className="text-center">คะแนนเฉลี่ย</th>
                                    <th className="text-center">น้ำหนัก</th>
                                    <th className="text-center">ผู้ตอบ</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(schoolData.scores?.eit2?.questions || []).map((q, index) => (
                                    <tr key={q.id || index}>
                                      <td className="text-center">{index + 1}</td>
                                      <td>{q.question_text || 'N/A'}</td>
                                      <td className="text-center">
                                        <span className={`badge ${getScoreBadgeClass(q.avg_score)}`}>
                                          {safeToFixed(q.avg_score)}
                                        </span>
                                      </td>
                                      <td className="text-center">{safeToFixed(q.weight)}</td>
                                      <td className="text-center">{schoolData.scores?.eit2?.respondent_count || 0}</td>
                                    </tr>
                                  ))}
                                  {(!schoolData.scores?.eit2?.questions || schoolData.scores.eit2.questions.length === 0) && (
                                    <tr>
                                      <td colSpan="5" className="text-center text-muted">ไม่มีข้อมูลคำถาม EIT2</td>
                                    </tr>
                                  )}
                                </tbody>
                                <tfoot>
                                  <tr className="table-info">
                                    <td colSpan="4" className="text-end"><strong>คะแนนรวม EIT2:</strong></td>
                                    <td className="text-center"><strong>{safeToFixed(schoolData.summary.eit2_score)}</strong></td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          </div>

                          {/* OIT Tab */}
                          <div className="tab-pane fade" id={`oit-${schoolData.school?.id || schoolIndex}`} role="tabpanel">
                            <div className="table-responsive mt-3">
                              <table className="table table-sm table-striped">
                                <thead>
                                  <tr>
                                    <th className="text-center">ข้อ</th>
                                    <th>คำถาม</th>
                                    <th className="text-center">คะแนนเฉลี่ย</th>
                                    <th className="text-center">น้ำหนัก</th>
                                    <th className="text-center">จำนวนการประเมิน</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(schoolData.scores?.oit?.questions || []).map((q, index) => (
                                    <tr key={q.question_id || index}>
                                      <td className="text-center">{index + 1}</td>
                                      <td>{q.question_text || 'N/A'}</td>
                                      <td className="text-center">
                                        <span className={`badge ${getScoreBadgeClass(q.avg_score)}`}>
                                          {safeToFixed(q.avg_score)}
                                        </span>
                                      </td>
                                      <td className="text-center">{safeToFixed(q.weight)}</td>
                                      <td className="text-center">{q.evaluations_count || 0}</td>
                                    </tr>
                                  ))}
                                  {(!schoolData.scores?.oit?.questions || schoolData.scores.oit.questions.length === 0) && (
                                    <tr>
                                      <td colSpan="5" className="text-center text-muted">ไม่มีข้อมูลคำถาม OIT</td>
                                    </tr>
                                  )}
                                </tbody>
                                <tfoot>
                                  <tr className="table-warning">
                                    <td colSpan="4" className="text-end"><strong>คะแนนรวม OIT:</strong></td>
                                    <td className="text-center"><strong>{safeToFixed(schoolData.summary.oit_score)}</strong></td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}

            {loadingReport && (
              <div className="card">
                <div className="card-body text-center py-5">
                  <div className="spinner-border text-primary me-2" style={{width: '3rem', height: '3rem'}}></div>
                  <h5 className="mt-3">กำลังโหลดรายงาน</h5>
                  <p className="text-muted">กรุณารอสักครู่ ระบบกำลังประมวลผลข้อมูล...</p>
                </div>
              </div>
            )}

            {!loadingReport && reportData.length === 0 && selectedFiscalYear && (
              <div className="card">
                <div className="card-body text-center py-5">
                  <i className="fas fa-info-circle fa-3x text-info mb-3"></i>
                  <h5>ไม่พบข้อมูลรายงาน</h5>
                  <p>ไม่พบข้อมูลคะแนนสำหรับปีงบประมาณและโรงเรียนที่เลือก</p>
                  <p className="text-muted">กรุณาตรวจสอบว่ามีการตอบแบบสอบถามและการประเมินแล้วหรือไม่</p>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
};

export default DetailedReports;